/**
 * Make a phone photo small enough to send from a shop on 4G.
 *
 * A counter photographs a chassis plate and an Aadhaar card with whatever
 * camera the phone has. On a current handset that is a 3–12 MB JPEG, and the
 * app was posting those bytes untouched: slow on a weak signal, and outright
 * refused above 12 MB with a message that only says the file is too big. The
 * Aadhaar QR does not need twelve megapixels to be read — it needs to be sharp,
 * and 1600px on the long edge is sharper than any QR reader requires.
 *
 * So: shrink in the browser before it ever goes near the network. A 4 MB photo
 * becomes roughly 300 KB, the upload finishes on a bad line, and the 12 MB wall
 * stops being something a counter can hit at all.
 *
 * It never fails a sale. If the browser cannot decode the image — an unusual
 * format, a PDF, a memory-starved phone — the original file is returned and the
 * upload carries on exactly as before.
 */

/** Long edge, in px, for an ordinary photo — a chassis plate, a bill. */
const MAX_EDGE = 1600;
const QUALITY = 0.82;

/**
 * An Aadhaar is not an ordinary photo.
 *
 * Its secure QR is a dense grid, and the reader needs roughly three pixels per
 * module to decode it. Across a whole card at 1600px the QR lands around 300px
 * wide — marginal before JPEG artefacts, hopeless after them. Shrinking every
 * upload to 1600/0.82 therefore made the EXACT reading fail and handed every
 * card to OCR, which guesses (owner, 16 Sep 2026: "still the ocr isnt
 * working"). A QR that decodes needs no OCR and no checking.
 *
 * 2600px at 0.92 is roughly 700 KB — still a tenth of what the camera produced,
 * and it keeps the QR readable.
 */
const AADHAAR_EDGE = 2600;
/**
 * The BACK of a card carries no QR — only the address, read by OCR, which is
 * prepared server-side at 2200px anyway. Sending it at 2600px/0.92 cost the
 * counter a two-megabyte upload on mobile data for nothing (owner, 16 Sep
 * 2026: "it is very slow"). The front keeps its full size: that is where the
 * QR is, and the QR is the exact read.
 */
const BACK_EDGE = 1800;
const BACK_QUALITY = 0.85;
const AADHAAR_QUALITY = 0.92;

/**
 * A flyer is looked at on a phone and shared on to customers (3 Oct 2026), so
 * it keeps more than a photo of a bill: a long edge a phone screen cannot
 * out-resolve, at a quality that keeps its print crisp. A designer's 8 MB
 * export comes down to a few hundred KB every counter can load on 4G.
 */
const FLYER_EDGE = 2048;
const FLYER_QUALITY = 0.88;

/** Below this there is nothing worth re-encoding. */
const LEAVE_ALONE_BELOW = 400 * 1024;

const isShrinkable = (file: File) => /^image\/(jpeg|png|webp)$/i.test(file.type);

/**
 * Where the card is in a photo: the bright, nearly grey rectangle. Worked out
 * on a small copy (a few hundred pixels across — the answer is a box, not a
 * picture) and mapped back to the full frame. Null when no card stands out,
 * or when the card IS the frame already.
 *
 * The same rule the server uses to read the card (18 Sep 2026): the photos a
 * phone's camera app hands back have the card filling a third of the frame,
 * and the print in that third is too small to read. Cut to the card before
 * shrinking, the card keeps every pixel the camera gave it.
 */
function cardBoxIn(bitmap: ImageBitmap): { x: number; y: number; w: number; h: number } | null {
  const probeWidth = 240;
  const scale = probeWidth / bitmap.width;
  const width = probeWidth;
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);
  const isCard = (i: number) => {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const max = Math.max(r, g, b);
    return max > 150 && max - Math.min(r, g, b) < 60;
  };
  const longestRun = (profile: number[], floor: number): [number, number] | null => {
    let best: [number, number] | null = null;
    let start = -1;
    for (let i = 0; i <= profile.length; i += 1) {
      const on = i < profile.length && profile[i]! >= floor;
      if (on && start < 0) start = i;
      if (!on && start >= 0) {
        if (!best || i - start > best[1] - best[0]) best = [start, i];
        start = -1;
      }
    }
    return best;
  };
  const rows = new Array<number>(height).fill(0);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) if (isCard((y * width + x) * 4)) rows[y] = rows[y]! + 1;
  }
  const ys = longestRun(rows, Math.round(width * 0.3));
  if (!ys) return null;
  const cols = new Array<number>(width).fill(0);
  for (let y = ys[0]; y < ys[1]; y += 1) {
    for (let x = 0; x < width; x += 1) if (isCard((y * width + x) * 4)) cols[x] = cols[x]! + 1;
  }
  const xs = longestRun(cols, Math.round((ys[1] - ys[0]) * 0.3));
  if (!xs) return null;
  const pad = Math.round(Math.min(width, height) * 0.03);
  const box = {
    x: Math.max(0, xs[0] - pad),
    y: Math.max(0, ys[0] - pad),
    w: Math.min(width, xs[1] - xs[0] + 2 * pad),
    h: Math.min(height, ys[1] - ys[0] + 2 * pad),
  };
  box.w = Math.min(box.w, width - box.x);
  box.h = Math.min(box.h, height - box.y);
  // Too small to be the card, or the whole frame already: leave the photo alone.
  const share = (box.w * box.h) / (width * height);
  if (share < 0.08 || share > 0.9 || box.w < width * 0.25 || box.h < height * 0.15) return null;
  /*
   * Card-shaped, or not at all (22 Oct 2026). An Aadhaar is 1.58 times as wide
   * as it is tall; a box far from that is a part of the card — the white below
   * its coloured band, a strip of glare — and keeping only that threw away the
   * name and the QR: uploads of 221×133 and 172×511 were on file. The whole
   * photo goes up instead, and the server finds the card in it.
   */
  const aspect = Math.max(box.w, box.h) / Math.max(1, Math.min(box.w, box.h));
  if (aspect < 1.3 || aspect > 1.9) return null;
  const full = {
    x: Math.round(box.x / scale),
    y: Math.round(box.y / scale),
    w: Math.round(box.w / scale),
    h: Math.round(box.h / scale),
  };
  // A crop smaller than a readable card is no better than the photo it came from.
  if (Math.max(full.w, full.h) < 600) return null;
  return full;
}

export async function shrinkForUpload(file: File, kind?: string): Promise<File> {
  // The two Aadhaar sides keep their detail; everything else is a photo of a
  // plate or a piece of paper and shrinks hard.
  const isAadhaar = kind === 'aadhaar_photo' || kind === 'aadhaar_back';
  const isBack = kind === 'aadhaar_back';
  const isFlyer = kind === 'flyer';
  const maxEdge = isFlyer ? FLYER_EDGE : isBack ? BACK_EDGE : isAadhaar ? AADHAAR_EDGE : MAX_EDGE;
  const quality = isFlyer ? FLYER_QUALITY : isBack ? BACK_QUALITY : isAadhaar ? AADHAAR_QUALITY : QUALITY;

  if (!isShrinkable(file)) return file;
  // An Aadhaar photo is always looked at, however small: it may need cutting
  // down to the card. Anything else below the floor is not worth re-encoding.
  if (!isAadhaar && file.size <= LEAVE_ALONE_BELOW) return file;
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const card = isAadhaar ? cardBoxIn(bitmap) : null;
    const sourceW = card ? card.w : bitmap.width;
    const sourceH = card ? card.h : bitmap.height;
    const longest = Math.max(sourceW, sourceH);
    if (!card && longest <= maxEdge && file.size <= (isAadhaar ? 4 : isFlyer ? 1.5 : 2) * 1024 * 1024) {
      bitmap.close?.();
      return file;
    }

    const scale = Math.min(1, maxEdge / longest);
    const width = Math.max(1, Math.round(sourceW * scale));
    const height = Math.max(1, Math.round(sourceH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    // Downscaling a photo without smoothing turns fine print — and a QR's
    // modules — into aliased mush.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // The result is a JPEG, which has no transparency: a transparent PNG flyer
    // would come out black-backed (3 Oct 2026). White behind it, as on paper.
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    if (card) {
      ctx.drawImage(bitmap, card.x, card.y, card.w, card.h, 0, 0, width, height);
    } else {
      ctx.drawImage(bitmap, 0, 0, width, height);
    }
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });
    // A card cut out of its photo is worth keeping even when it is not smaller.
    if (!blob || (!card && blob.size >= file.size)) return file;

    const name = file.name.replace(/\.[^.]+$/, '') || (isFlyer ? 'flyer' : 'photo');
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    // A photo that will not decode is still a photo the server may accept.
    return file;
  }
}

/** "4.2 MB" — for the one message a counter should still be able to hit. */
export function describeSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
